/**
 * React hooks for Medications API
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { medicationsApi } from '../api/medications';
import { toast } from 'sonner';

export function useMedications(patientId: number) {
  return useQuery({
    queryKey: ['medications', patientId],
    queryFn: () => medicationsApi.getMedications(patientId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

export function useLogMedication(patientId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ medicationId, data }: {
      medicationId: number;
      data: {
        medication_schedule_id: number;
        scheduled_time: string;
        taken_time?: string;
        status: 'taken' | 'missed' | 'skipped';
        date: string;
        notes?: string;
      };
    }) => medicationsApi.logMedication(patientId, medicationId, data),
    
    onMutate: async ({ medicationId, data }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['medications', patientId] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData(['medications', patientId]);
      
      // Optimistically update
      queryClient.setQueryData(['medications', patientId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          today_logs: [
            ...old.today_logs,
            {
              id: Date.now(),
              medication_id: medicationId,
              ...data,
              logged_at: new Date().toISOString(),
            },
          ],
        };
      });
      
      return { previousData };
    },
    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications', patientId] });
      queryClient.invalidateQueries({ queryKey: ['adherence', patientId] });
      toast.success('Medication logged successfully');
    },
    
    onError: (error: any, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['medications', patientId], context.previousData);
      }
      toast.error(error.message || 'Failed to log medication');
    },
  });
}

export function useMedicationHistory(patientId: number, days: number = 30) {
  return useQuery({
    queryKey: ['medication-history', patientId, days],
    queryFn: () => medicationsApi.getMedicationHistory(patientId, days),
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

export function useCreateMedicationSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: medicationsApi.createMedicationSchedule,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['medications', data.patient_id] });
      toast.success('Medication schedule created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create medication schedule');
    },
  });
}

/**
 * ✅ NEW: Get current patient's medication logs
 */
export function useMyMedicationLogs(days: number = 7) {
  return useQuery({
    queryKey: ['my-medication-logs', days],
    queryFn: () => medicationsApi.getMyMedicationLogs(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * ✅ NEW: Update medication log for current patient user
 */
export function useUpdateMyMedicationLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ logId, data }: {
      logId: number;
      data: {
        scheduled_time?: string;
        taken_time?: string;
        status?: 'taken' | 'missed' | 'skipped';
        notes?: string;
      };
    }) => medicationsApi.updateMyMedicationLog(logId, data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-medication-logs'] });
      toast.success('Medication log updated successfully');
    },

    onError: (error: any) => {
      toast.error(error.message || 'Failed to update medication log');
    },
  });
}

/**
 * ✅ NEW: Delete medication log for current patient user
 */
export function useDeleteMyMedicationLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (logId: number) => medicationsApi.deleteMyMedicationLog(logId),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-medication-logs'] });
      toast.success('Medication log deleted successfully');
    },

    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete medication log');
    },
  });
}